import { useGlobalAPIQueryContext } from "@dev2_techwalnut/tw-api-query-hook";
import { useAPIQuery } from "../../../../hooks/useAPIQuery";
import React, { useEffect, useState, useRef } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import MissingPODropdown from "../MissingPODropdown";
import moment from "moment";
import secureLocalStorage from "react-secure-storage";
import StoreAllotBatchModal from "./store-allot-batch-modal";
import Swal from "sweetalert2";
import {
    resolveAllotmentRemaining,
    trimDecimals,
} from "../../../utils/trim-decimal";

const IssueSlipStore = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { invalidateApi } = useGlobalAPIQueryContext();
    const privileges = secureLocalStorage.getItem("privileges");
    const userID = secureLocalStorage.getItem("userID");
    const requisitionInfo = location.state?.data;
    const { date, machine } = location.state;

    const [itemIds, setItemIds] = useState([]);
    const [batchesData, setBatchesData] = useState({});

    // Track allocations by batch number instead of index
    const allocationsRef = useRef({});

    const methods = useForm({
        mode: "onBlur",
    });

    // Sorting state
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: null,
    });

    const { register, handleSubmit, reset, control, watch, formState } = methods;
    const { errors } = formState;

    const { fields: slips } = useFieldArray({
        control,
        name: `slips`,
    });

    const { useFetch: useFetchBatches } = useAPIQuery({
        key: "api/batch",
        endpoint: "api/batch",
    });

    const { data: batches, isLoading: loadingBatches } = useFetchBatches({
        params: {
            items: itemIds?.join(","),
        },
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 3,
        retry: 3,
        keepPlaceholder: true,
        refetchOnReconnect: true,
        enabled: !!itemIds,
    });

    const {
        useFetch: useFetchRequisitionSlips,
        useCreate: useUpdateRequisitionSlips,
    } = useAPIQuery({
        key: `requisition-slip-${requisitionInfo?.id}`,
        endpoint: "/api/requisitions/",
    });

    const {
        data: requisitionSlips,
        isLoading: requisitionSlipsLoading,
        isError: errorLoadingRequisitionSlips,
        isFetching: fetchingRequisitionSlips,
    } = useFetchRequisitionSlips({
        params: {
            action: "date_wise_slip_department",
            date: date,
            machine_id: machine,
        },
        abortOnNewRequest: true,
        retry: 3,
    });

    const { mutate: updateRequisitionSlips, isPending } =
        useUpdateRequisitionSlips({
            params: {
                action: "approval-store-flow",
                department: "store",
            },
            errorMessageFromServer: true,
            onSuccess: (res) => {
                Swal.fire({
                    icon: "success",
                    title: "Success",
                    text: res?.message,
                }).then(() => {
                    invalidateApi({ key: "api/batch" });
                    navigate(-1);
                });
                reset();
            },
        });

    // Recalculate batch quantities based on all current allocations
    const recalculateBatchQuantities = () => {
        if (!batches?.results?.data) return;

        const allSlips = watch("slips") || [];

        // Calculate total allocations per batch
        const batchAllocations = {};

        allSlips.forEach((slip) => {
            slip.batches?.forEach((batchAllocation) => {
                const itemId = batchAllocation?.batch?.item_id;
                const batchNumber = batchAllocation?.batch?.value;
                const quantity = trimDecimals(
                    Number(batchAllocation?.allotted_quantity) || 0
                );

                if (itemId && batchNumber && quantity > 0) {
                    const key = `${itemId}-${batchNumber}`;
                    batchAllocations[key] = trimDecimals(
                        (batchAllocations[key] || 0) + quantity
                    );
                }
            });
        });

        // Update allocations reference
        allocationsRef.current = batchAllocations;

        // Apply allocations to batch data
        const updatedBatchData = {};

        Object.keys(batches.results.data).forEach((itemId) => {
            updatedBatchData[itemId] = batches.results.data[itemId].map(
                (batch) => {
                    const key = `${itemId}-${batch.batch_number}`;
                    const allocated = batchAllocations[key] || 0;
                    const originalQuantity = batch.received_quantity;

                    return {
                        ...batch,
                        received_quantity: Math.max(
                            0,
                            trimDecimals(originalQuantity - allocated)
                        ),
                    };
                }
            );
        });

        setBatchesData(updatedBatchData);
    };

    // Updated function to handle batch updates
    const updateBatch = () => {
        // Simply recalculate everything from current form state
        recalculateBatchQuantities();
    };

    const onSubmit = (data) => {
        const hasInvalidAllotment = data?.slips?.some(
            (_, index) => errors?.slips?.[index]?.invalid_allotment
        );
        if (hasInvalidAllotment) {
            Swal.fire({
                icon: "error",
                title: "Cannot allot more than requested",
                text: "Reduce allotted quantity so it does not exceed remaining requested quantity.",
            });
            return;
        }

        const res = data?.slips?.map((d) => ({
            department: d?.department,
            item_id: d?.item_id,
            requisition_ids: d?.requisition_ids,
            approved_by_allotting_department:
                d?.approved_by_allotting_department ? 1 : 2,
            received_quantity: Number(
                d?.batches?.reduce(
                    (sum, b) => sum + (b?.allotted_quantity || 0),
                    0
                )
            ),
            batches: d?.batches?.map((batch) => ({
                batch_no: batch?.batch?.value,
                received_quantity: batch?.allotted_quantity,
            })),
        }));

        const response = {
            bulk_upload: res,
            updated_by: userID,
        };

        console.log(response);
        updateRequisitionSlips({ data: response });
    };

    // Sorting function
    const handleSort = (key) => {
        let direction = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });
    };

    // Get sorted slips
    const getSortedSlips = () => {
        if (!slips || slips.length === 0) return [];

        const sortedSlips = [...slips];

        if (sortConfig.key) {
            sortedSlips.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                // Handle null/undefined values
                if (aValue == null) aValue = "";
                if (bValue == null) bValue = "";

                // Convert to string for comparison
                aValue = String(aValue).toLowerCase();
                bValue = String(bValue).toLowerCase();

                if (aValue < bValue) {
                    return sortConfig.direction === "asc" ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === "asc" ? 1 : -1;
                }
                return 0;
            });
        }

        return sortedSlips;
    };

    const sortedSlips = getSortedSlips();

    // Sort icon component
    const SortIcon = ({ columnKey }) => {
        if (sortConfig.key !== columnKey) {
            return <span style={{ marginLeft: "5px", opacity: 0.3 }}>↕</span>;
        }
        return (
            <span style={{ marginLeft: "5px" }}>
                {sortConfig.direction === "asc" ? "↑" : "↓"}
            </span>
        );
    };

    useEffect(() => {
        if (batches) {
            // Initialize with original batch data
            const originalData = batches?.results?.data || {};
            setBatchesData(originalData);

            // Recalculate based on any existing allocations
            setTimeout(() => {
                recalculateBatchQuantities();
            }, 0);
        }
    }, [batches]);

    const resolveStoreSlips = (payload) => {
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.results?.data)) return payload.results.data;
        if (Array.isArray(payload?.data)) return payload.data;
        return [];
    };

    useEffect(() => {
        if (requisitionSlips) {
            const storeSlips = resolveStoreSlips(requisitionSlips);
            setItemIds(storeSlips?.map((rs) => rs?.item?.id));
            reset({
                slips: storeSlips?.map((rs) => {
                    const remainingQuantity = resolveAllotmentRemaining(rs);
                    return {
                        department: rs?.department,
                        item_id: rs?.item?.id,
                        item_name: rs?.item?.item_name,
                        daily_plan_code: rs?.daily_plan?.daily_plan_code,
                        requested_quantity: rs?.requested_quantity,
                        received_quantity: rs?.received_quantity,
                        remaining_quantity: remainingQuantity,
                        rows: rs?.rows || [],
                        stock_quantity:
                            rs?.available_stock != null
                                ? Number(rs.available_stock)
                                : rs?.item?.stock_quantity,
                        item_on_hand:
                            rs?.item_on_hand ?? rs?.item?.stock_quantity,
                        locked_stock: rs?.locked_stock ?? 0,
                        approved_by_allotting_department:
                            remainingQuantity <= 0 ||
                            (rs?.rows?.every(
                                (row) =>
                                    row.approved_by_allotting_department === 1
                            ) ??
                                false),
                        approved_by_requesting_department:
                            rs?.approved_by_requesting_department === 1
                                ? true
                                : false,
                        uom: rs?.item?.uom,
                        batches: [],
                        category: rs?.item?.category,
                        requisition_ids: rs?.rows?.map((r) => r?.id),
                    };
                }),
            });
        }
    }, [requisitionSlips, reset]);

    return (
        <React.Fragment>
            <div className="nk-block-head nk-block-head-sm">
                <div className="nk-block-between">
                    <div className="nk-block-head-content d-flex">
                        <h3 className="nk-block-title page-title">
                            Issue Slip
                        </h3>
                    </div>
                </div>
            </div>
            <div className="card">
                <FormProvider {...methods}>
                    <form
                        onSubmit={handleSubmit(onSubmit)}
                        autoComplete="off"
                        noValidate
                    >
                        <div className="card-inner p-0 m-0">
                            <div className="card-body table-responsive p-0 pb-0">
                                <table className="table table-bordered">
                                    <thead>
                                        <tr>
                                            <th
                                                colSpan={11}
                                                className="text-center"
                                            >
                                                Green Edge Venture
                                            </th>
                                        </tr>
                                        <tr>
                                            <th
                                                colSpan={11}
                                                className="text-center"
                                            >
                                                Issue slip
                                            </th>
                                        </tr>
                                        <tr>
                                            <th
                                                colSpan={11}
                                                className="text-end"
                                            >
                                                {`Date: ${moment(
                                                    requisitionInfo?.date
                                                ).format("DD-MM-YYYY")}`}
                                            </th>
                                        </tr>
                                        <tr>
                                            <th
                                                colSpan={8}
                                                className="text-center"
                                            >
                                                Store Department
                                            </th>
                                        </tr>
                                        <tr>
                                            <th
                                                className="text-center"
                                                onClick={() =>
                                                    handleSort("department")
                                                }
                                                style={{
                                                    cursor: "pointer",
                                                    userSelect: "none",
                                                }}
                                            >
                                                Department
                                                <SortIcon columnKey="department" />
                                            </th>
                                            <th
                                                className="text-center"
                                                onClick={() =>
                                                    handleSort("item_name")
                                                }
                                                style={{
                                                    cursor: "pointer",
                                                    userSelect: "none",
                                                }}
                                            >
                                                Material
                                                <SortIcon columnKey="item_name" />
                                            </th>
                                            <th className="text-center">
                                                Req. Qty
                                            </th>
                                            <th className="text-center">
                                                Issue Qty
                                            </th>
                                            <th className="text-center">
                                                Available Stock
                                            </th>
                                            <th className="text-center">
                                                Batches
                                            </th>
                                            <th className="text-center">UOM</th>
                                            <th className="text-center">
                                                Issue
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedSlips?.length > 0 &&
                                        !errorLoadingRequisitionSlips ? (
                                            sortedSlips?.map((slip, index) => {
                                                const originalIndex =
                                                    slips.findIndex(
                                                        (s) => s.id === slip.id
                                                    );
                                                return (
                                                    <tr key={index}>
                                                        <td className="text-center">
                                                            {slip?.department}
                                                        </td>
                                                        <td className="text-center">
                                                            {slip?.item_name}
                                                        </td>
                                                        <td className="text-center">
                                                            {
                                                                slip?.requested_quantity
                                                            }
                                                        </td>
                                                        <td className="text-center">
                                                            <p className="text-success">
                                                                <strong className="me-1">
                                                                    previously
                                                                    issued
                                                                    quantity:
                                                                </strong>
                                                                {trimDecimals(
                                                                    slip?.received_quantity,
                                                                    5
                                                                )}
                                                            </p>
                                                            <p className="text-info">
                                                                <strong className="me-1">
                                                                    Current
                                                                    alloted:
                                                                </strong>
                                                                {watch(
                                                                    `slips.${index}.batches`
                                                                )?.reduce(
                                                                    (
                                                                        total,
                                                                        batch
                                                                    ) =>
                                                                        total +
                                                                        (Number(
                                                                            batch?.allotted_quantity
                                                                        ) || 0),
                                                                    0
                                                                )}
                                                            </p>
                                                        </td>
                                                        <td className="text-center">
                                                            <p className="mb-0">
                                                                {trimDecimals(
                                                                    slip?.stock_quantity,
                                                                    5
                                                                )}
                                                            </p>
                                                            {Number(
                                                                slip?.locked_stock ||
                                                                    0
                                                            ) > 0.0001 ? (
                                                                <small className="text-warning">
                                                                    locked{" "}
                                                                    {trimDecimals(
                                                                        slip?.locked_stock,
                                                                        5
                                                                    )}
                                                                </small>
                                                            ) : null}
                                                        </td>

                                                        <td>
                                                            <div className="d-flex justify-content-start align-items-center">
                                                                <div>
                                                                    {resolveAllotmentRemaining(
                                                                        slip
                                                                    ) <= 0 ? (
                                                                        <span className="text-success">
                                                                            Allotted
                                                                        </span>
                                                                    ) : (
                                                                        <StoreAllotBatchModal
                                                                            row={
                                                                                slip
                                                                            }
                                                                            batches={
                                                                                batchesData
                                                                            }
                                                                            loadingBatches={
                                                                                loadingBatches
                                                                            }
                                                                            index={
                                                                                index
                                                                            }
                                                                            updateBatch={
                                                                                updateBatch
                                                                            }
                                                                        />
                                                                    )}
                                                                </div>
                                                                {slip?.category?.toLowerCase() ===
                                                                "raw material" ? (
                                                                    <div>
                                                                        <MissingPODropdown
                                                                            itemIds={
                                                                                itemIds
                                                                            }
                                                                            item={
                                                                                slip?.item_id
                                                                            }
                                                                        />
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                        </td>
                                                        <td className="text-center">
                                                            {slip?.uom}
                                                        </td>
                                                        <td className="text-center">
                                                            <span
                                                                className="text-success"
                                                                style={{
                                                                    display:
                                                                        slip?.approved_by_allotting_department
                                                                            ? "block"
                                                                            : "none",
                                                                }}
                                                            >
                                                                Issued
                                                            </span>
                                                            <input
                                                                type="checkbox"
                                                                {...register(
                                                                    `slips.${originalIndex}.approved_by_allotting_department`
                                                                )}
                                                                disabled={
                                                                    !privileges.includes(
                                                                        "store_dept_slip_update"
                                                                    ) ||
                                                                    slip?.approved_by_allotting_department
                                                                }
                                                                style={{
                                                                    display:
                                                                        slip?.approved_by_allotting_department
                                                                            ? "none"
                                                                            : "block",
                                                                }}
                                                            />
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : !(
                                              requisitionSlipsLoading ||
                                              fetchingRequisitionSlips
                                          ) ? (
                                            <tr>
                                                <td
                                                    colSpan={8}
                                                    className="text-center"
                                                >
                                                    {errorLoadingRequisitionSlips
                                                        ? "Error loading data"
                                                        : "No data to display"}
                                                </td>
                                            </tr>
                                        ) : null}
                                        {(requisitionSlipsLoading ||
                                            fetchingRequisitionSlips) && (
                                            <tr>
                                                <td
                                                    colSpan={9}
                                                    className="text-center"
                                                >
                                                    Loading data...
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className="col-12 m-3 ">
                                <div className="form-group">
                                    <button
                                        type="button"
                                        className="btn btn-md btn-light me-2"
                                        id="submit-button-backbutton"
                                        onClick={() => navigate(-1)}
                                    >
                                        Back
                                    </button>
                                    {isPending ? (
                                        <button
                                            type="button"
                                            id="button-typemaster-loading"
                                            className="btn btn-md text-dark"
                                            disabled
                                        >
                                            Loading...
                                        </button>
                                    ) : (
                                        <button
                                            type="submit"
                                            className="btn btn-md btn-primary"
                                            id="submit-button-create-typemaster"
                                            disabled={
                                                requisitionSlipsLoading ||
                                                errorLoadingRequisitionSlips ||
                                                resolveStoreSlips(
                                                    requisitionSlips
                                                ).length === 0 ||
                                                slips?.some(
                                                    (_, index) =>
                                                        errors?.slips?.[index]
                                                            ?.invalid_allotment
                                                )
                                            }
                                        >
                                            Submit
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </form>
                </FormProvider>
            </div>
        </React.Fragment>
    );
};

export default IssueSlipStore;
